import { ScoringKnobs, BoxscorePlayerStats } from '../../types';

export type Position = 'K' | 'P';
export type StatFormat = 'int' | 'dec1';

export interface ScoreColumn {
  key: string;
  label: string;
  position: Position;
  statField?: keyof BoxscorePlayerStats;
  derive?: (s: BoxscorePlayerStats) => number;
  enabledBy: (keyof ScoringKnobs)[];
  format: StatFormat;
}

// Registry — order = display order. enabledBy: any non-zero knob shows the column.
const COLUMNS: ScoreColumn[] = [
  // Kicker
  { key: 'fgU40', label: 'FG<40', position: 'K', statField: 'fg_made_under_40', enabledBy: ['fg_under_40'], format: 'int' },
  { key: 'fg40', label: 'FG40+', position: 'K', statField: 'fg_made_40_49', enabledBy: ['fg_40_49'], format: 'int' },
  { key: 'fg50', label: 'FG50+', position: 'K', statField: 'fg_made_50_plus', enabledBy: ['fg_50_plus'], format: 'int' },
  { key: 'fg60', label: 'FG60+', position: 'K', statField: 'fg_made_60_plus', enabledBy: ['fg_60_plus'], format: 'int' },
  { key: 'ep', label: 'EP', position: 'K', statField: 'extra_points_made', enabledBy: ['extra_point'], format: 'int' },
  { key: 'miss', label: 'Miss', position: 'K', derive: s => (s.fg_attempts || 0) - (s.fg_made || 0), enabledBy: ['fg_miss'], format: 'int' },
  { key: 'gw', label: 'GW', position: 'K', statField: 'game_winning_kicks', enabledBy: ['game_winner_bonus'], format: 'int' },
  { key: 'clutch', label: 'Clutch', position: 'K', statField: 'clutch_kicks', enabledBy: ['clutch_kick_bonus'], format: 'int' },
  { key: 'ko20', label: 'KO In20', position: 'K', statField: 'kickoffs_inside_20', enabledBy: ['kickoff_inside_20'], format: 'int' },
  { key: 'kotb', label: 'KO TB', position: 'K', statField: 'kickoff_touchbacks', enabledBy: ['kickoff_touchback'], format: 'int' },
  { key: 'koob', label: 'KO OOB', position: 'K', statField: 'kickoff_out_of_bounds', enabledBy: ['kickoff_out_of_bounds'], format: 'int' },
  { key: 'onside', label: 'Onside', position: 'K', statField: 'onside_recovered', enabledBy: ['onside_recovery_bonus'], format: 'int' },
  { key: 'fgEpa', label: 'FG EPA', position: 'K', statField: 'fg_epa', enabledBy: ['fg_epa_multiplier'], format: 'dec1' },
  { key: 'xpa', label: 'xPA', position: 'K', statField: 'fg_xpa', enabledBy: ['fg_xpa_multiplier'], format: 'dec1' },
  // Punter
  { key: 'in20', label: 'In20', position: 'P', statField: 'punts_inside_20', enabledBy: ['punt_inside_20'], format: 'int' },
  { key: 'in10', label: 'In10', position: 'P', statField: 'punts_inside_10', enabledBy: ['punt_inside_10'], format: 'int' },
  { key: 'in5', label: 'In5', position: 'P', statField: 'punts_inside_5', enabledBy: ['punt_inside_5'], format: 'int' },
  { key: 'long', label: 'Long', position: 'P', statField: 'longest_punt', enabledBy: ['punt_50_plus', 'punt_60_plus'], format: 'int' },
  { key: 'net', label: 'Net', position: 'P', statField: 'net_punt_average', enabledBy: ['punt_net_avg_40', 'punt_net_avg_45'], format: 'dec1' },
  { key: 'tb', label: 'TB', position: 'P', statField: 'touchbacks', enabledBy: ['touchback_penalty'], format: 'int' },
  { key: 'fc', label: 'FC', position: 'P', statField: 'fair_catches', enabledBy: ['fair_catch_bonus'], format: 'int' },
  { key: 'rtd', label: 'RetTD', position: 'P', statField: 'punt_return_tds', enabledBy: ['punt_return_td_penalty'], format: 'int' },
  { key: 'puntEpa', label: 'Punt EPA', position: 'P', statField: 'punt_epa', enabledBy: ['punt_epa_multiplier'], format: 'dec1' },
];

export const enabledColumns = (position: Position, knobs: ScoringKnobs): ScoreColumn[] =>
  COLUMNS.filter(c => c.position === position && c.enabledBy.some(k => !!Number(knobs[k])));

export const formatStat = (value: number, format: StatFormat): string => {
  if (!value) return '–';
  if (format === 'dec1') {
    return (Math.round((value + Number.EPSILON) * 10) / 10).toFixed(1);
  }
  return String(value);
};
